import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideNativeDateAdapter } from '@angular/material/core';
import { provideRouter, TitleStrategy, withComponentInputBinding } from '@angular/router';
import {
  atAuthInterceptor,
  AUTH_CONFIG,
  AuthConfig,
  ConfigurableAuthService,
  PageVisibilityService,
  provideAuthenticatedRedirectUrl,
  provideErrorDictionary,
} from '@anothertrip/anothertrip-ds';

import { environment } from '../environments/environment';

import { provideBillingAccountApiConfig } from './api/billing-account/billing-account-api.config';
import { provideCollaborationApiConfig } from './api/collaboration/collaboration-api.config';
import { provideCreatorCampaignsApiConfig } from './api/creator-campaigns/creator-campaigns-api.config';
import { provideInsightsApiConfig } from './api/insights/insights-api.config';
import { provideInstagramApiConfig } from './api/instagram/instagram-api.config';
import { provideInvoiceApiConfig } from './api/invoice/invoice-api.config';
import { provideMapApiConfig } from './api/map/map-api.config';
import { provideMarketplaceApiConfig } from './api/marketplace/marketplace-api.config';
import { providePlacesApiConfig } from './api/places/places-api.config';
import { provideReferralApiConfig } from './api/referral/referral-api.config';
import { provideSubscriptionsApiConfig } from './api/subscriptions/subscriptions-api.config';
import { provideTaxApiConfig } from './api/tax/tax-api.config';
import { provideTaxAuditApiConfig } from './api/tax-audit/tax-audit-api.config';
import { routes } from './app.routes';
import { instagramInterceptor } from './interceptors/instagram/instagram.interceptor';
import { paywallInterceptor } from './paywall/interceptors/paywall.interceptor';
import { PaywallService } from './paywall/services/paywall.service';
import { AppTitleStrategy } from './routes/app-title.strategy';
import { LogoutService } from './services/logout.service';
import { UserService } from './services/user/user.service';

const authConfig: AuthConfig = {
  apiBaseUrl: `${environment.apiUrl}/v1/creator`,
  endpoints: {
    login: 'login',
    requestPasswordReset: 'request-password-reset',
    resetPasswordNewUser: 'reset-password-new-user',
    waitlist: 'waitlist',
    logout: 'logout',
    instagramOAuth: 'instagram/oauth',
    userInfo: 'user-info',
    refreshToken: 'refresh-token',
    confirmCode: 'confirm-code',
    changePassword: 'change-password',
    sendConfirmationCode: 'send-confirmation-code',
    deleteAccount: 'delete-account',
  },
  customHeaders: {
    'client-id': environment.clientId,
  },
  unauthenticatedRedirectRoute: '/login',
  bypassTokenEndpoints: [
    '/login',
    '/register',
    '/public-api',
    '/confirm-code',
    '/reset-password',
    '/request-password-reset',
    '/waitlist',
    '/send-confirmation-code',
    '/oauth2/code',
    '/oauth2/token',
    'cloudfunctions.net',
    'unsplash.com',
    'facebook.com',
    'anothertrip-tax-audit-evidence',
  ],
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(routes, withComponentInputBinding()),
    // Every titled route names itself in the document title: `{route title} | AnotherTrip Creator`.
    { provide: TitleStrategy, useClass: AppTitleStrategy },
    provideHttpClient(withFetch(), withInterceptors([atAuthInterceptor, paywallInterceptor, instagramInterceptor])),
    {
      provide: AUTH_CONFIG,
      useValue: authConfig,
    },
    provideAppInitializer(() => {
      const userService = inject(UserService);
      const paywallService = inject(PaywallService);
      inject(PageVisibilityService).initializeVisibilityService();
      inject(LogoutService).initialize();

      // Without Remember me a link opened in a new tab takes its session from an open tab; read the user after that
      return inject(ConfigurableAuthService).sessionRestored.then(() => {
        userService.loadUser();
        paywallService.initialize();
      });
    }),
    provideErrorDictionary(),
    provideInstagramApiConfig({
      baseUrl: `${environment.apiUrl}/v1/creator/instagram`,
      endpoints: {
        syncMedia: 'media/sync',
        listMedia: 'media',
        autoRespond: 'media/auto-respond',
        autoResponses: (mediaId) => `media/${mediaId}/auto-responses`,
        createAutoResponse: (mediaId) => `media/${mediaId}/auto-responses`,
        updateAutoResponse: (mediaId, responseId) => `media/${mediaId}/auto-responses/${responseId}`,
        deleteAutoResponse: (mediaId, responseId) => `media/${mediaId}/auto-responses/${responseId}`,
        defaultAutoResponses: 'auto-responses',
      },
    }),
    provideMapApiConfig({
      baseUrl: `${environment.apiUrl}/v1/creator/maps`,
      endpoints: {
        generate: 'generate',
        status: (requestId) => `${requestId}/status`,
        activities: (requestId) => `${requestId}/activities`,
        createActivity: (requestId) => `${requestId}/activities`,
        updateActivity: (requestId, activityId) => `${requestId}/activities/${activityId}`,
        deleteActivity: (requestId, activityId) => `${requestId}/activities/${activityId}`,
        activityPhotos: (requestId, activityId) => `${requestId}/activities/${activityId}/photos`,
        updateActivityVisibility: (requestId, activityId) => `${requestId}/activities/${activityId}/visibility`,
        reorderActivities: (requestId) => `${requestId}/activities/order`,
        bookingCampaign: (requestId) => `${requestId}/booking-campaign`,
        bookingLinkSuggestions: (requestId) => `${requestId}/booking-link-suggestions`,
        regenerateMap: (requestId) => `${requestId}/regenerate`,
        deleteMap: (requestId) => `${requestId}`,
      },
    }),
    provideInsightsApiConfig({
      baseUrl: `${environment.apiUrl}/v1/creator/insights`,
      endpoints: {
        followersGrowth: 'instagram/followers-growth',
        summary: 'summary',
        postsOverview: 'posts-overview',
      },
    }),
    providePlacesApiConfig({
      baseUrl: `${environment.apiUrl}/v1/creator/places`,
      endpoints: {
        searchPlaces: 'search',
      },
    }),
    provideMarketplaceApiConfig({
      baseUrl: `${environment.apiUrl}/v1/creator/marketplace`,
      endpoints: {
        getOptIn: 'opt-in',
        updateOptIn: 'opt-in',
        getCampaigns: 'campaigns',
        getCampaign: (campaignId) => `campaigns/${campaignId}`,
        applyCampaign: (campaignId) => `campaigns/${campaignId}/apply`,
        withdrawApplication: (campaignId) => `campaigns/${campaignId}/withdraw`,
        getApplications: 'applications',
      },
    }),
    provideCreatorCampaignsApiConfig({
      baseUrl: `${environment.apiUrl}/v1/creator`,
      endpoints: {
        listMyCampaigns: 'campaigns',
        getCreatorCampaign: (campaignId) => `campaigns/${campaignId}`,
        respondToCampaign: (campaignId) => `campaigns/${campaignId}/respond`,
        getCampaignBookingLinks: (campaignId) => `campaigns/${campaignId}/booking-links`,
      },
    }),
    provideTaxApiConfig({
      baseUrl: `${environment.apiUrl}/v1/creator/tax`,
      endpoints: {
        getProfile: 'profile',
        updateProfile: 'profile',
        deleteProfile: 'profile',
        getSupportedCountries: 'supported-countries',
      },
    }),
    provideTaxAuditApiConfig({
      baseUrl: `${environment.apiUrl}/v1/creator/tax-audit`,
      endpoints: {
        getSummary: 'summary',
        requestAudit: 'request',
        getRecords: 'records',
        updateStatus: 'status',
        getUploadUrl: 'evidence/upload-url',
        getKeywords: 'keywords',
        addCustomKeyword: 'keywords/custom',
        deleteCustomKeyword: (keywordId) => `keywords/custom/${keywordId}`,
        getJobStatus: (jobId) => `jobs/${jobId}`,
        deleteEvidence: (auditId, documentId) => `${auditId}/evidence/${documentId}`,
        getComplianceInfo: 'compliance-info',
        getComplianceHealth: 'compliance-health',
        generateReport: 'report',
      },
    }),
    provideCollaborationApiConfig({
      baseUrl: `${environment.apiUrl}/v1/creator/collaborations`,
      endpoints: {
        getCollaborations: '',
        getCollaboration: (collaborationId) => `${collaborationId}`,
        createCollaboration: '',
        updateCollaboration: (collaborationId) => `${collaborationId}`,
        acceptCollaboration: (collaborationId) => `${collaborationId}/accept`,
        updateCollaborationStatus: (collaborationId) => `${collaborationId}/status`,
        getBenefitTypes: 'benefit-types',
        generateCollaborationInvoices: (collaborationId) => `${collaborationId}/invoices`,
        addCollaborationEvidence: (collaborationId) => `${collaborationId}/evidence`,
        updateCollaborationEvidence: (collaborationId, evidenceId) => `${collaborationId}/evidence/${evidenceId}`,
        deleteCollaborationEvidence: (collaborationId, evidenceId) => `${collaborationId}/evidence/${evidenceId}`,
        devImport: 'dev-import',
        dismissAllDetected: 'review/dismiss-all',
        clearUnconfirmed: 'review/clear',
        resolveReviewPost: (shortcode) => `review/posts/${shortcode}/resolve`,
      },
    }),
    provideInvoiceApiConfig({
      baseUrl: `${environment.apiUrl}/v1/creator/invoices`,
      endpoints: {
        getInvoice: (invoiceId) => `${invoiceId}`,
        getInvoices: '',
        createInvoice: '',
      },
    }),
    provideBillingAccountApiConfig({
      baseUrl: `${environment.apiUrl}/v1/creator/billing`,
      endpoints: {
        getPaymentMethod: 'payment-method',
        updatePaymentMethod: 'payment-method',
        createSetupIntent: 'payment-method/setup-intent',
        getReceipts: 'receipts',
      },
    }),
    provideSubscriptionsApiConfig({
      baseUrl: `${environment.apiUrl}/v1/creator/subscriptions`,
      endpoints: {
        getSubscriptions: '',
        getActiveSubscription: 'active',
        createSubscription: 'create',
        cancelSubscription: (subscriptionId) => `${subscriptionId}`,
        getPricingPlans: 'pricing-plans',
        getSubscription: (subscriptionId) => `${subscriptionId}`,
        getSubscriptionStatus: 'status',
      },
    }),
    provideReferralApiConfig({
      baseUrl: `${environment.apiUrl}/v1/creator/referral`,
      endpoints: {
        getCode: 'code',
        getStats: 'stats',
      },
    }),
    provideNativeDateAdapter(),
    provideAuthenticatedRedirectUrl('/dashboard'),
  ],
};
